import { forwardRef, type ChangeEvent } from "react"
import { cn } from "@/lib/utils"

export interface PhoneInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  onChange?: (value: string) => void
}

/**
 * Aplica máscara de telefone brasileiro: (41) 98765-4321
 * Formato: (DD) NNNNN-NNNN (celular 9 dígitos) ou (DD) NNNN-NNNN (fixo 8 dígitos)
 */
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "")
  const limited = digits.slice(0, 11)

  if (limited.length === 0) return ""

  let formatted = "("

  if (limited.length >= 1) {
    formatted += limited.slice(0, 2)
  }

  if (limited.length >= 2) {
    formatted += ") "
  }

  if (limited.length > 2) {
    const remaining = limited.slice(2)

    if (remaining.length <= 5) {
      formatted += remaining
    } else {
      const isCell = remaining.length > 8
      const firstPart = isCell ? 5 : Math.min(remaining.length - 4, 4)

      formatted += remaining.slice(0, firstPart)

      if (remaining.length > firstPart) {
        formatted += "-" + remaining.slice(firstPart)
      }
    }
  }

  return formatted
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, onChange, value, ...props }, ref) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhone(event.target.value)
      onChange?.(formatted)
    }

    return (
      <input
        ref={ref}
        type="tel"
        inputMode="numeric"
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        value={value}
        onChange={handleChange}
        placeholder="(41) 98765-4321"
        {...props}
      />
    )
  }
)

PhoneInput.displayName = "PhoneInput"

export { PhoneInput, formatPhone }
